import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from 'i18next';
import type { RootState } from '../store';
import {
  collectAssistantCandidateEnglishFromPayload,
  collectAssistantCandidateNamesFromPayload,
  generateAssistantMessageId,
  mapHunterHistoryToMessages,
  mergeAssistantHistoryMessages,
  readMiniChatOpen,
  writeMiniChatOpen,
} from '../../utils/assistant.utils';
import { AssistantState } from './Assistant.type';
import {
  askAssistantThunk,
  clearAssistantHistoryThunk,
  deleteAssistantHistoryThunk,
  hydrateAssistantCandidateNamesThunk,
  loadAssistantHistoryThunk,
} from './Assistant.thunk';

const mergeCacheById = (
  current: Record<string, string | null>,
  incoming: Record<string, string | null | undefined>,
) => {
  const entries = Object.entries(incoming).filter(
    (entry): entry is [string, string | null] => entry[1] !== undefined,
  );

  if (!entries.length) {
    return current;
  }

  const next = { ...current };

  for (const [id, value] of entries) {
    if (value === null && next[id]) {
      continue;
    }

    next[id] = value;
  }

  return next;
};

const initialState: AssistantState = {
  messages: [],
  isLoading: false,
  askOrigin: null,
  miniChatOpen: readMiniChatOpen(),
  historyHydrated: false,
  historyHasMore: false,
  historyNextPage: 1,
  historyLoadingMore: false,
  historyClearing: false,
  nameById: {},
  englishById: {},
  namesLoadingIds: [],
};

export const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    cancelAssistantAskAction: state => {
      state.isLoading = false;
      state.askOrigin = null;
    },
    clearAssistantThreadAction: state => {
      state.messages = [];
      state.isLoading = false;
      state.askOrigin = null;
      state.historyHydrated = false;
      state.historyHasMore = false;
      state.historyNextPage = 1;
      state.historyLoadingMore = false;
      state.historyClearing = false;
      state.nameById = {};
      state.englishById = {};
      state.namesLoadingIds = [];
    },
    setAssistantMiniChatOpenAction: (state, action: PayloadAction<boolean>) => {
      state.miniChatOpen = action.payload;
      writeMiniChatOpen(action.payload);
    },
  },
  extraReducers: builder => {
    builder.addCase(loadAssistantHistoryThunk.pending, (state, action) => {
      if (action.meta.arg?.loadMore) {
        state.historyLoadingMore = true;
      }
    });
    builder.addCase(loadAssistantHistoryThunk.fulfilled, (state, action) => {
      const { items, pagination } = action.payload;
      const incoming = mapHunterHistoryToMessages(items);
      const candidates = items.flatMap(item => item.candidates ?? []);
      const payloadNames = collectAssistantCandidateNamesFromPayload(candidates);
      const payloadEnglish =
        collectAssistantCandidateEnglishFromPayload(candidates);

      state.historyNextPage = pagination.current_page + 1;
      state.historyHasMore = pagination.current_page < pagination.last_page;
      state.historyHydrated = true;
      state.historyLoadingMore = false;
      state.messages = mergeAssistantHistoryMessages(state.messages, incoming);
      state.nameById = mergeCacheById(state.nameById, payloadNames);
      state.englishById = mergeCacheById(state.englishById, payloadEnglish);
    });
    builder.addCase(loadAssistantHistoryThunk.rejected, (state, action) => {
      state.historyHydrated = true;
      state.historyLoadingMore = false;

      if (!action.meta.arg?.loadMore) {
        state.historyHasMore = false;
      }
    });
    builder.addCase(askAssistantThunk.pending, (state, action) => {
      state.isLoading = true;
      state.askOrigin = action.meta.arg.origin;
      const lastMessage = state.messages[state.messages.length - 1];

      if (
        lastMessage?.role === 'user' &&
        lastMessage.text === action.meta.arg.question
      ) {
        return;
      }

      state.messages.push({
        id: generateAssistantMessageId(),
        role: 'user',
        text: action.meta.arg.question,
        status: 'complete',
      });
    });
    builder.addCase(askAssistantThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      const historyId = action.payload.historyId;
      const question = action.meta.arg.question;

      if (historyId) {
        for (let index = state.messages.length - 1; index >= 0; index -= 1) {
          const message = state.messages[index];

          if (message.role === 'user' && message.text === question) {
            message.id = `${historyId}-user`;
            break;
          }
        }
      }

      state.messages.push({
        id: historyId
          ? `${historyId}-assistant`
          : generateAssistantMessageId(),
        role: 'assistant',
        text: action.payload.message ?? '',
        candidates: action.payload.candidates ?? [],
        status: 'complete',
      });
      state.nameById = mergeCacheById(
        state.nameById,
        collectAssistantCandidateNamesFromPayload(action.payload.candidates),
      );
      state.englishById = mergeCacheById(
        state.englishById,
        collectAssistantCandidateEnglishFromPayload(action.payload.candidates),
      );
    });
    builder.addCase(askAssistantThunk.rejected, (state, action) => {
      if (action.payload?.aborted) {
        return;
      }

      state.isLoading = false;
      state.messages.push({
        id: generateAssistantMessageId(),
        role: 'assistant',
        text: i18n.t(action.payload?.messageKey || 'assistant.error', {
          defaultValue: i18n.t('assistant.error'),
        }),
        status: 'error',
      });
    });
    builder.addCase(deleteAssistantHistoryThunk.fulfilled, (state, action) => {
      const historyId = action.payload;
      const userId = `${historyId}-user`;
      const assistantId = `${historyId}-assistant`;

      state.messages = state.messages.filter(
        message => message.id !== userId && message.id !== assistantId,
      );
    });
    builder.addCase(clearAssistantHistoryThunk.pending, state => {
      state.historyClearing = true;
    });
    builder.addCase(clearAssistantHistoryThunk.fulfilled, state => {
      state.messages = [];
      state.isLoading = false;
      state.askOrigin = null;
      state.historyHydrated = true;
      state.historyHasMore = false;
      state.historyNextPage = 1;
      state.historyLoadingMore = false;
      state.historyClearing = false;
      state.nameById = {};
      state.englishById = {};
      state.namesLoadingIds = [];
    });
    builder.addCase(clearAssistantHistoryThunk.rejected, state => {
      state.historyClearing = false;
    });
    builder.addCase(
      hydrateAssistantCandidateNamesThunk.pending,
      (state, action) => {
        const loadingSet = new Set(state.namesLoadingIds);

        for (const id of action.meta.arg) {
          const normalized = String(id).trim();

          if (normalized && !loadingSet.has(normalized)) {
            state.namesLoadingIds.push(normalized);
            loadingSet.add(normalized);
          }
        }
      },
    );
    builder.addCase(
      hydrateAssistantCandidateNamesThunk.fulfilled,
      (state, action) => {
        const requested = new Set(action.meta.arg.map(id => String(id).trim()));
        state.namesLoadingIds = state.namesLoadingIds.filter(
          id => !requested.has(id),
        );
        state.nameById = mergeCacheById(state.nameById, action.payload.names);
        state.englishById = mergeCacheById(
          state.englishById,
          action.payload.english,
        );
      },
    );
    builder.addCase(
      hydrateAssistantCandidateNamesThunk.rejected,
      (state, action) => {
        const requested = new Set(action.meta.arg.map(id => String(id).trim()));
        state.namesLoadingIds = state.namesLoadingIds.filter(
          id => !requested.has(id),
        );
      },
    );
  },
});

export const {
  cancelAssistantAskAction,
  clearAssistantThreadAction,
  setAssistantMiniChatOpenAction,
} = assistantSlice.actions;

export const getAssistantMessages = (state: RootState) =>
  state.assistant.messages;
export const getAssistantIsLoading = (state: RootState) =>
  state.assistant.isLoading;
export const getAssistantAskOrigin = (state: RootState) =>
  state.assistant.askOrigin;
export const getAssistantMiniChatOpen = (state: RootState) =>
  state.assistant.miniChatOpen;
export const getAssistantHistoryHasMore = (state: RootState) =>
  state.assistant.historyHasMore;
export const getAssistantHistoryLoadingMore = (state: RootState) =>
  state.assistant.historyLoadingMore;
export const getAssistantHistoryClearing = (state: RootState) =>
  state.assistant.historyClearing;
export const getAssistantCandidateCachedName = (
  state: RootState,
  candidateId?: string | null,
) => (candidateId ? state.assistant.nameById[candidateId] : undefined);
export const getAssistantCandidateCachedEnglish = (
  state: RootState,
  candidateId?: string | null,
) => (candidateId ? state.assistant.englishById[candidateId] : undefined);

export default assistantSlice.reducer;
